import React, { ReactNode } from 'react';
import { useAppSelector } from 'hooks';
import classNames from 'classnames';

import Icon, { IconProp } from '~components/icon';
import TooltipPortal from '~weekly-planner/components/TooltipPortal';

import { selectUserTags } from '~main/selectors/login';

interface ComponentProps {
  id: string | number;
  icon: IconProp;
  shape?: 'square' | 'round';
  size?: 'x-small' | 'small' | 'medium' | 'large';
  toolTip?: ReactNode;
  onClick?: (val?: any) => void;
  color?: string | null;
  className?: string;
  iconClassname?: string;
  disabled?: boolean;
  showTooltipWhenDisabled?: boolean;
  dataCy?: string;
  authTag?: string;
}
/**
 * A clickable icon component with optional tooltip and customizable size/shape.
 * @param {string | number} props.id - Unique identifier for the icon.
 * @param {IconProp} props.icon - The icon to display (passed to the `Icon` component).
 * @param {'square' | 'round'} [props.shape='round'] - Defines the icon's shape. Default is `'round'`.
 * @param {'small' | 'medium' | 'large'} [props.size='small'] - Controls the icon size. Default is `'small'`.
 * @param {ReactNode} [props.toolTip] - Tooltip text or component to display on hover.
 * @param {() => void} [props.onClick] - Click handler for the icon.
 * @param {boolean} [props.disabled]- Disabled the icon
 * @param {string | null} [props.color='black'] - Color of the icon, default is `'black'`, can be null for css styling.
 * @param {string | null} [props.authTag] Authorization tag that restricts button visibility to users with this tag
 * @returns {JSX.Element} The clickable icon component.
 */

export const ClickableIcon: React.FC<ComponentProps> = ({
  id,
  icon,
  className: wrapperClassName = '',
  iconClassname = '',
  shape = 'round',
  size = 'small',
  toolTip,
  onClick,
  color = 'black',
  disabled = false,
  showTooltipWhenDisabled = false,
  dataCy,
  authTag,
}) => {
  const tags = useAppSelector(selectUserTags);
  const visible = !authTag || (authTag && tags.includes(authTag));

  const getSizeClasses = (size: 'x-small' | 'small' | 'medium' | 'large') => ({
    sizeClass: `icon-${size}`,
    clickableSizeClass: `clickable-${size}`,
  });

  const { sizeClass, clickableSizeClass } = getSizeClasses(size);

  const shapeClass = shape === 'round' ? 'icon-round' : 'icon-square';

  const target = `clickable-icon-${id}`;
  const className = classNames(
    [sizeClass, iconClassname],
    { [`text-${color}`]: color !== null },
    { 'icon-disabled': disabled },
    { disabled },
  );
  const wrapperClassNames = classNames(['clickable-icon', clickableSizeClass, shapeClass, wrapperClassName], {
    disabled: disabled && !showTooltipWhenDisabled,
    'disabled-no-hover': disabled && showTooltipWhenDisabled,
  });

  const handleClick = (event: React.MouseEvent) => {
    if (disabled) {
      event?.preventDefault?.();
      return;
    }
    onClick?.();
  };

  if (!visible) return null;

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      data-cy={dataCy}
      className={wrapperClassNames}
      id={target}
      onClick={handleClick}
      onKeyDown={({ key }) => {
        if (!disabled && (key === 'Enter' || key === ' ')) {
          onClick?.();
        }
      }}
      style={{ userSelect: 'none' }}
    >
      {toolTip && (!disabled || showTooltipWhenDisabled) && <TooltipPortal targetId={target} content={toolTip} />}
      <Icon icon={icon} id={`icon-${size}`} className={className} />
    </div>
  );
};

export default ClickableIcon;
